import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ShieldCheck, Loader2, Lock } from "lucide-react";
import { PRICE } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
    { name: "first_name", label: "First Name", type: "text", placeholder: "Jordan", testid: "checkout-first-name-input" },
    { name: "last_name", label: "Last Name", type: "text", placeholder: "Lee", testid: "checkout-last-name-input" },
    { name: "email", label: "Email", type: "email", placeholder: "you@example.com", testid: "checkout-email-input" },
    { name: "phone", label: "Mobile Phone", type: "tel", placeholder: "+1 555 000 0000", testid: "checkout-phone-input" },
];

const inputCls = "w-full bg-white/5 border border-white/10 focus:border-orchid/70 focus:bg-white/[0.07] outline-none px-4 py-4 text-base text-white placeholder-zinc-600 transition-colors duration-300";

export const CheckoutSection = () => {
    const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "" });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.first_name.trim() || !form.last_name.trim() || !/\S+@\S+\.\S+/.test(form.email) || form.phone.trim().length < 5) {
            setError("Please complete all fields with a valid email and phone number.");
            return;
        }
        setLoading(true);
        try {
            localStorage.setItem("coa_customer", JSON.stringify(form));
            const { data } = await axios.post(`${API}/payments/checkout`, {
                ...form,
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (err) {
            setError(err.response?.data?.detail || "Could not start checkout. Please try again.");
            setLoading(false);
        }
    };

    return (
        <section id="checkout" data-testid="checkout-section" className="relative py-24 sm:py-32 lg:py-40 overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(139,92,246,0.18),transparent_55%)] pointer-events-none" />
            <div className="absolute -bottom-32 -left-32 w-[45vw] h-[45vw] rounded-full bg-gold/10 blur-[110px] pointer-events-none" />
            <div className="relative max-w-2xl mx-auto px-5 sm:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="gold-border-beam bg-[#120A24] p-6 sm:p-12 shadow-[0_30px_120px_rgba(124,58,237,0.25)]"
                >
                    <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold mb-4">Secure Checkout</p>
                    <h2 className="font-serif uppercase font-medium tracking-tight text-3xl sm:text-4xl leading-tight mb-3">
                        Get Instant Access
                    </h2>
                    <p className="text-sm text-zinc-400 mb-8 sm:mb-10">
                        Digital book + weekly live webinar — <span className="text-gold font-medium">{PRICE}</span> one time.
                    </p>

                    <form data-testid="checkout-form" onSubmit={onSubmit} className="space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            {FIELDS.slice(0, 2).map((f) => (
                                <div key={f.name}>
                                    <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">{f.label}</label>
                                    <input data-testid={f.testid} name={f.name} type={f.type} value={form[f.name]} onChange={onChange} placeholder={f.placeholder} className={inputCls} required />
                                </div>
                            ))}
                        </div>
                        {FIELDS.slice(2).map((f) => (
                            <div key={f.name}>
                                <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">{f.label}</label>
                                <input data-testid={f.testid} name={f.name} type={f.type} value={form[f.name]} onChange={onChange} placeholder={f.placeholder} className={inputCls} required />
                            </div>
                        ))}

                        {error && (
                            <p data-testid="checkout-error" className="text-sm text-red-400 border border-red-400/30 bg-red-400/5 px-4 py-3">{error}</p>
                        )}

                        <button
                            data-testid="checkout-submit-button"
                            type="submit"
                            disabled={loading}
                            className="group relative w-full overflow-hidden bg-gold text-black font-mono text-xs sm:text-sm tracking-[0.2em] uppercase px-8 py-5 hover:bg-gold-light transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-[0_10px_50px_rgba(168,85,247,0.3)]"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                            {loading ? "Redirecting to Secure Payment…" : `Get Instant Access — ${PRICE}`}
                        </button>
                    </form>

                    <div data-testid="checkout-payment-methods" className="mt-8 flex items-center justify-center gap-3 text-zinc-500 font-mono text-[10px] tracking-[0.2em] uppercase text-center">
                        <ShieldCheck className="w-4 h-4 text-orchid shrink-0" />
                        <span>Apple Pay · Google Pay · Credit / Debit Card</span>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};
